import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PlusIcon } from '@heroicons/react/24/outline'
import { Card, Title, Text } from '@tremor/react'
import { useMyContext } from '../ContextProviders/CreditsContextProvider'
import { getTeamUuid } from '../../utils/cookies'
import { updateCredits } from '../../Requests/TeamRequests'
import SavedArticlesTable from '../ArticleBibliography/SavedArticlesTable'
import ArticleSearchForm from '../ArticleBibliography/ArticleSearchForm'
import SearchResultsList from '../ArticleBibliography/SearchResultsList'
import SearchHistory from '../ArticleBibliography/SearchHistory'
import ManualArticleModal from '../ArticleBibliography/ManualArticleModal'
import { getSavedArticles } from '../../Requests/ArticleBibliographyRequests'

function BibliographyTab({ projectUuid }) {
    const { t } = useTranslation()
    const { credits, setCredits } = useMyContext()
    
    const [savedArticles, setSavedArticles] = useState([])
    const [savedArticlesLoading, setSavedArticlesLoading] = useState(true)
    const [savedArticlesPagination, setSavedArticlesPagination] = useState(null)
    const [currentSearch, setCurrentSearch] = useState(null)
    const [searchResults, setSearchResults] = useState([])
    const [searchLoading, setSearchLoading] = useState(false)
    const [refreshTrigger, setRefreshTrigger] = useState(0)
    const [showCreateModal, setShowCreateModal] = useState(false)
    const [activeSubTab, setActiveSubTab] = useState(0) // 0: Saved Articles, 1: Searches

    useEffect(() => {
        if (projectUuid) {
            loadSavedArticles()
            updateCreditsData()
        }
    }, [projectUuid, refreshTrigger])

    const loadSavedArticles = async (page = 1) => {
        try {
            setSavedArticlesLoading(true)
            const response = await getSavedArticles(getTeamUuid(), projectUuid, page)
            setSavedArticles(response.data.data)
            setSavedArticlesPagination({
                current_page: response.data.current_page,
                last_page: response.data.last_page,
                per_page: response.data.per_page,
                total: response.data.total
            })
        } catch (error) {
            console.error('Error loading saved articles:', error)
            setSavedArticles([])
        } finally {
            setSavedArticlesLoading(false)
        }
    }

    const updateCreditsData = async () => {
        try {
            const response = await updateCredits(getTeamUuid())
            setCredits(response.data)
        } catch (error) {
            console.error('Error updating credits:', error)
        }
    }

    const handleArticleSaved = () => {
        setRefreshTrigger(prev => prev + 1)
        updateCreditsData()
    }

    const handleArticleDeleted = () => {
        setRefreshTrigger(prev => prev + 1)
    }

    const handleSearchComplete = (searchData, results) => {
        setCurrentSearch(searchData)
        setSearchResults(results)
        updateCreditsData()
    }

    const handleArticleCreated = () => {
        setRefreshTrigger(prev => prev + 1)
    }

    // Subtabs configuration
    const subtabs = [
        { 
            id: 0, 
            name: t('articleBibliography.subtabs.savedArticles'), 
            current: activeSubTab === 0 
        },
        { 
            id: 1, 
            name: t('articleBibliography.subtabs.searches'), 
            current: activeSubTab === 1 
        }
    ]

    const classNames = (...classes) => {
        return classes.filter(Boolean).join(' ')
    }

    return (
        <div className="mt-4 space-y-6">
            {/* Top Separator */}
            <div className="border-t border-gray-200"></div>
            
            {/* Subtabs Navigation */}
            <div className="flex flex-col lg:flex-row justify-between gap-2 whitespace-nowrap">
                <div className="sm:hidden">
                    <select
                        id="subtabs"
                        name="subtabs"
                        className="block w-full rounded-md border-gray-300 focus:border-black focus:ring-black"
                        onChange={(e) => setActiveSubTab(Number(e.target.value))}
                        value={activeSubTab}
                    >
                        {subtabs.map((tab) => (
                            <option key={tab.id} value={tab.id}>{tab.name}</option>
                        ))}
                    </select>
                </div>
                <div className="hidden sm:block">
                    <nav className="flex gap-2 flex-wrap" aria-label="Bibliography Tabs">
                        {subtabs.map((tab) => (
                            <button
                                key={tab.id}
                                className={classNames(
                                    tab.current ? 'bg-black text-white' : 'text-gray-500 hover:text-gray-700 border border-black',
                                    'rounded-md px-3 py-2 text-xs font-medium whitespace-nowrap'
                                )}
                                aria-current={tab.current ? 'page' : undefined}
                                onClick={() => setActiveSubTab(tab.id)}
                            >
                                {tab.name}
                            </button>
                        ))}
                    </nav>
                </div>
            </div>
            {/* Tab Content */}
            {activeSubTab === 0 && (
                /* Saved Articles Section */
                <Card>
                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <Title>{t('articleBibliography.savedArticles.title')}</Title>
                            <Text className="mt-1">{t('articleBibliography.savedArticles.subtitle')}</Text>
                        </div>
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium"
                        >
                            <PlusIcon className="w-4 h-4" />
                            {t('articleBibliography.manualCreate.createButton')}
                        </button>
                    </div>
                    <SavedArticlesTable
                        articles={savedArticles}
                        loading={savedArticlesLoading}
                        pagination={savedArticlesPagination}
                        onPageChange={loadSavedArticles}
                        onArticleDeleted={handleArticleDeleted}
                    />
                </Card>
            )}

            {activeSubTab === 1 && (
                <div className="space-y-6">
                    {/* Article Search Section */}
                    <Card>
                        <div className="mb-6">
                            <div className="flex gap-2 items-center">
                                <Title>{t('articleBibliography.search.title')}</Title>
                                <span className='bg-black rounded-full text-white px-4 py-0.5 text-sm animate-pulse'>Beta</span>
                            </div>
                            <Text className="mt-1">{t('articleBibliography.search.subtitle')}</Text>
                        </div>
                        
                        <div className="space-y-6">
                            <ArticleSearchForm
                                credits={credits}
                                onSearchComplete={handleSearchComplete}
                                loading={searchLoading}
                                setLoading={setSearchLoading}
                            />
                            
                            {(currentSearch || searchResults.length > 0) && (
                                <SearchResultsList
                                    currentSearch={currentSearch}
                                    results={searchResults}
                                    projectId={projectUuid}
                                    onArticleSaved={handleArticleSaved}
                                    loading={searchLoading}
                                />
                            )}
                        </div>
                    </Card>

                    {/* Search History Section */}
                    <Card>
                        <SearchHistory onArticleSaved={handleArticleSaved} />
                    </Card>
                </div>
            )}

            {/* Manual Article Creation Modal */}
            <ManualArticleModal
                open={showCreateModal}
                setOpen={setShowCreateModal}
                projectUuid={projectUuid}
                onArticleCreated={handleArticleCreated}
            />
        </div>
    )
}

export default BibliographyTab